import { analytics } from '../utils/analytics';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: Date;
  level: LogLevel;
  message: string;
  metadata?: Record<string, any>;
  sessionId?: string;
  userId?: string;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private readonly maxLogs = 1000;
  private sessionId: string;
  private userId?: string;
  private logFilters: ((entry: LogEntry) => boolean)[] = [];
  private logEnhancers: ((entry: LogEntry) => LogEntry)[] = [];
  private remoteLoggingEnabled = false;
  private remoteLoggingEndpoint?: string;
  private remoteLoggingBatchSize = 10;
  private remoteLoggingQueue: LogEntry[] = [];
  private remoteLoggingInterval?: number;

  private constructor() {
    this.sessionId = `session-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    this.setupRemoteLogging();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private setupRemoteLogging() {
    // Check if we're in production by looking at the import.meta.env
    const isDevelopment = import.meta.env.DEV;
    this.remoteLoggingEnabled = !isDevelopment;
    this.remoteLoggingEndpoint = import.meta.env.VITE_REMOTE_LOGGING_ENDPOINT;
    
    if (this.remoteLoggingEnabled && this.remoteLoggingEndpoint) {
      // Set up periodic flushing of logs
      this.remoteLoggingInterval = window.setInterval(() => {
        this.flushRemoteLogs();
      }, 30000); // Flush every 30 seconds
    }
  }

  private flushRemoteLogs() {
    if (!this.remoteLoggingEnabled || !this.remoteLoggingEndpoint || this.remoteLoggingQueue.length === 0) {
      return;
    }

    const logsToSend = this.remoteLoggingQueue.splice(0, this.remoteLoggingBatchSize);
    
    // For now, just simulate remote logging
    if (import.meta.env.DEV) {
      console.log(`[Remote Logging] Would send ${logsToSend.length} logs to ${this.remoteLoggingEndpoint}`);
    }
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  clearUserId() {
    this.userId = undefined;
  }

  addLogFilter(filter: (entry: LogEntry) => boolean) {
    this.logFilters.push(filter);
  }

  addLogEnhancer(enhancer: (entry: LogEntry) => LogEntry) {
    this.logEnhancers.push(enhancer);
  }

  debug(message: string, metadata?: Record<string, any>) {
    this.log('debug', message, metadata);
  }

  info(message: string, metadata?: Record<string, any>) {
    this.log('info', message, metadata);
  }

  warn(message: string, metadata?: Record<string, any>) {
    this.log('warn', message, metadata);
  }

  error(message: string, metadata?: Record<string, any>) {
    this.log('error', message, metadata);
  }

  private log(level: LogLevel, message: string, metadata?: Record<string, any>) {
    let entry: LogEntry = {
      timestamp: new Date(),
      level,
      message,
      metadata: {
        ...metadata,
        url: window.location.href,
      },
      sessionId: this.sessionId,
      userId: this.userId,
    };

    // Apply enhancers
    for (const enhancer of this.logEnhancers) {
      entry = enhancer(entry);
    }

    // Apply filters
    for (const filter of this.logFilters) {
      if (!filter(entry)) {
        return; // Skip this log entry
      }
    }

    // Add to local logs
    this.logs.unshift(entry);

    // Trim logs if they exceed max size
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Console logging in development
    if (import.meta.env.DEV) {
      this.consoleLog(entry);
    }

    // Add to remote logging queue if enabled
    if (this.remoteLoggingEnabled && (level === 'error' || level === 'warn')) {
      this.remoteLoggingQueue.push(entry);
      
      // Flush immediately for errors
      if (level === 'error') {
        this.flushRemoteLogs();
      }
    }

    // Track in analytics for important events
    if (level === 'error' || (metadata && metadata.trackEvent)) {
      analytics.trackEvent({
        category: 'Log',
        action: level,
        label: message.substring(0, 100), // Truncate long messages
        metadata: {
          ...metadata,
          level,
          timestamp: entry.timestamp,
        },
      });
    }
  }

  private consoleLog(entry: LogEntry) {
    const timestamp = entry.timestamp.toISOString();
    const metadata = entry.metadata ? `\n${JSON.stringify(entry.metadata, null, 2)}` : '';
    const userInfo = entry.userId ? ` [User: ${entry.userId}]` : '';

    switch (entry.level) {
      case 'debug':
        console.debug(`[${timestamp}]${userInfo} 🔍 ${entry.message}${metadata}`);
        break;
      case 'info':
        console.info(`[${timestamp}]${userInfo} ℹ️ ${entry.message}${metadata}`);
        break;
      case 'warn':
        console.warn(`[${timestamp}]${userInfo} ⚠️ ${entry.message}${metadata}`);
        break;
      case 'error':
        console.error(`[${timestamp}]${userInfo} ❌ ${entry.message}${metadata}`);
        break;
    }
  }

  getLogs(options?: { level?: LogLevel; from?: Date; to?: Date; search?: string }): LogEntry[] {
    let filteredLogs = this.logs;

    if (options?.level) {
      filteredLogs = filteredLogs.filter(log => log.level === options.level);
    }

    if (options?.from) {
      filteredLogs = filteredLogs.filter(log => log.timestamp >= options.from!);
    }

    if (options?.to) {
      filteredLogs = filteredLogs.filter(log => log.timestamp <= options.to!);
    }

    if (options?.search) {
      const searchLower = options.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log => 
        log.message.toLowerCase().includes(searchLower) || 
        JSON.stringify(log.metadata).toLowerCase().includes(searchLower)
      );
    }

    return filteredLogs;
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'csv') {
      const headers = ['timestamp', 'level', 'message', 'metadata', 'sessionId', 'userId'];
      const rows = this.logs.map(log => [
        log.timestamp.toISOString(),
        log.level,
        log.message.replace(/,/g, ';'), // Escape commas
        JSON.stringify(log.metadata).replace(/,/g, ';'), // Escape commas
        log.sessionId || '',
        log.userId || '',
      ]);
      return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    }

    return JSON.stringify(this.logs, null, 2);
  }

  // Get log statistics
  getLogStats() {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    
    const recentLogs = this.logs.filter(log => log.timestamp > oneHourAgo);
    
    const logsByLevel = recentLogs.reduce((acc, log) => {
      acc[log.level] = (acc[log.level] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return {
      total: this.logs.length,
      recent: recentLogs.length,
      byLevel: logsByLevel,
    };
  }

  // Clean up resources
  dispose() {
    if (this.remoteLoggingInterval) {
      clearInterval(this.remoteLoggingInterval);
    }
    
    // Flush any remaining logs
    this.flushRemoteLogs();
  }
}

export const logger = Logger.getInstance();